import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";

import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { FaPlus } from "react-icons/fa";
import Slider from "react-slick";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import cardIcon from "../../../assets/card-icon.png";
import magnifyingGlassIcon from "../../../assets/lupa-faqs.png";
import Card from "../../../components/Card";
import AddCardModal from "../../../components/creditCard/modals/addCard";
import { CreditCardV2 } from "../../../components/CreditCardV2/CreditCardV2";
import { StatusCard } from "../../../components/CreditCardV2/StatusCard";
import LoadingElevaty from "../../../components/LoadingElevaty";
import { usePermissions } from "../../../Context/permissionContext";
import { useClient } from "../../../hooks/ClientProvider";
import useDependentCard from "../../../hooks/useDependentCard";
import useHolderCard from "../../../hooks/useHolderCard";
import { invalidCardStatus } from "../../../utils/cardStatus";
import * as S from "../style";
import CardMenu from "./CardMenu";
import HistoryBlockAction from "./HistoryBlockAction";
import CardsInTransition from "./CardsInTransition";
import ConfirmAction from "./modals/confirmAction";
import { Tabs } from "../../../components-v2/Tabs";

const ThumbnailsMenu = ({
  sliderRef,
  cards,
  currentSlide,
  onClick,
  title,
  showAddButton,
  onAddButtonTooltip,
  onAddClick,
  showArrows = false,
  getThumbnailTestId,
  navigationTestId,
}) => {
  const thumbnailsRef = useRef(null);

  const moveLeft = () => {
    sliderRef?.current?.slickPrev();
  };

  const moveRight = () => {
    sliderRef?.current?.slickNext();
  };

  useEffect(() => {
    if (!thumbnailsRef.current || isNaN(currentSlide)) return;

    const leftPosition = currentSlide > 0 ? currentSlide * 48 : 0;
    thumbnailsRef.current.scrollTo({
      left: leftPosition,
      behavior: "smooth",
    });
  }, [currentSlide]);

  return (
    <div className="thumbnails-menu-container">
      <h2>{title}</h2>

      <div className="thumbnails-container" ref={thumbnailsRef}>
        {showAddButton && (
          <Tooltip title={onAddButtonTooltip}>
            <button className="plus-rounded" type="button" onClick={onAddClick}>
              <FaPlus />
            </button>
          </Tooltip>
        )}

        {showArrows && (
          <span
            type="button"
            className="thumbnail-navigate"
            onClick={moveLeft}
            data-testid={
              navigationTestId ? `${navigationTestId}_anterior` : undefined
            }
          >
            <CaretLeftOutlined />
          </span>
        )}

        {cards.map((card, index) => (
          <div
            key={card.id}
            className={`thumbnail ${index === currentSlide ? "active" : ""}`}
            onClick={() => onClick(index)}
            data-testid={getThumbnailTestId?.(card)}
          >
            <img src={cardIcon} alt="Thumbnail" className="thumbnail-icon" />
          </div>
        ))}

        {showArrows && (
          <span
            type="button"
            className="thumbnail-navigate"
            onClick={moveRight}
            data-testid={
              navigationTestId ? `${navigationTestId}_proximo` : undefined
            }
          >
            <CaretRightOutlined />
          </span>
        )}
      </div>
    </div>
  );
};

function CardsComponent() {
  const { permissions } = usePermissions();
  const { client, refreshClientV2 } = useClient();

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentSlideDependent, setCurrentSlideDependent] = useState(0);
  const [currentSlideHistory, setCurrentSlideHistory] = useState(0);

  const sliderRef = useRef();
  const sliderRefDependent = useRef();
  const sliderRefHistory = useRef();

  const isProdutoBandeirado = client?.client?.isProdutoBandeirado;

  const {
    loadingHolderCard,
    holderCards,
    invalidCardsHistory,
    bloqCard,
    unBloqCard,
    cancelCard,
    getDataFormatedCards,
  } = useHolderCard(
    client?.client?.id,
    client?.product?.codigoProduto,
    client?.client?.cpf
  );

  const {
    dependantCards,
    reloadDependantCards,
    bloqCardDependent,
    unBloqCardDependent,
  } = useDependentCard(
    client?.client?.adicional,
    client?.product?.codigoProduto
  );

  const reloadAllCards = useCallback(async () => {
    await Promise.all([
      getDataFormatedCards?.(),
      reloadDependantCards?.(),
    ]);
  }, [getDataFormatedCards, reloadDependantCards]);

  const handleAfterVipagem = useCallback(async () => {
    if (refreshClientV2) await refreshClientV2();
    await reloadAllCards();
  }, [refreshClientV2, reloadAllCards]);

  function orderCardByStatus(a, b) {
    if (a.status === "ATIVO" && b.status !== "ATIVO") return -1;
    if (b.status === "ATIVO" && a.status !== "ATIVO") return 1;
    return 0;
  }

  const sortedHolderCards = useMemo(() => {
    const arr = [...holderCards];
    return arr.sort(orderCardByStatus);
  }, [holderCards]);

  const sortedDependentCards = useMemo(() => {
    const arr = [...dependantCards];
    return arr.sort(orderCardByStatus);
  }, [dependantCards]);

  const sortedHistoryCards = useMemo(() => {
    const arr = [...invalidCardsHistory];
    return arr.sort(orderCardByStatus);
  }, [invalidCardsHistory]);

  const settingsHolder = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: false,
    centerMode: true,
    centerPadding: "10px",
    afterChange: (index) => setCurrentSlide(index),
  };

  const settingsDependent = {
    ...settingsHolder,
    afterChange: (index) => setCurrentSlideDependent(index),
  };

  const settingsHistory = {
    ...settingsHolder,
    afterChange: (index) => setCurrentSlideHistory(index),
  };

  const toggleAddModal = useCallback(() => {
    setIsAddModalVisible((state) => !state);
  }, []);

  const handleGenerateCardHolder = useCallback(() => {
    setIsGenerateModalVisible(true);
  }, []);

  return (
    <>
      <S.WrapperCards>
        {loadingHolderCard ? (
          <S.WrapperNewLoading className="ant-card-bordered">
            <LoadingElevaty />
          </S.WrapperNewLoading>
        ) : (
          <Card modalTitle="Cartões" loading={loadingHolderCard}>
            <Tabs
              tabs={[
                {
                  id: "tab-cartao-titular",
                  title: "Titular",
                  content: (
                    <S.CreditCardWrapper>
                      {sortedHolderCards.length === 0 ? (
                        <div className="adicional__card">
                          <div className="adicional__card--header">
                            {permissions && permissions.includes("add") && (
                              <>
                                <div>
                                  <h2>Cartões</h2>
                                </div>
                                <div>
                                  <Tooltip title="Adicionar Cartão Titular">
                                    <button
                                      className="plus-rounded"
                                      type="button"
                                      onClick={handleGenerateCardHolder}
                                    >
                                      <FaPlus />
                                    </button>
                                  </Tooltip>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="adicional__card--bar" />
                          <div className="adicional__card--container">
                            <div className="adicional__card--content">
                              <div>
                                <img
                                  src={magnifyingGlassIcon}
                                  alt="Lupa"
                                  className="magnifier-icon"
                                />
                              </div>
                              <p>Não possui cartão cadastrado </p>
                              {permissions && permissions.includes("add") && (
                                <button
                                  type="button"
                                  aria-label="btn-add-new"
                                  onClick={handleGenerateCardHolder}
                                >
                                  <span>
                                    <FaPlus /> Adicionar Titular
                                  </span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {sortedHolderCards.length > 1 ? (
                            <>
                              <ThumbnailsMenu
                                sliderRef={sliderRef}
                                cards={sortedHolderCards}
                                currentSlide={currentSlide}
                                onClick={(index) =>
                                  sliderRef.current.slickGoTo(index)
                                }
                                title={
                                  sortedHolderCards.length > 1
                                    ? "Cartões"
                                    : "Cartão"
                                }
                                showAddButton={permissions?.includes(
                                  "view_button_generate_card_holder"
                                )}
                                onAddButtonTooltip="Adicionar Cartão Titular"
                                onAddClick={handleGenerateCardHolder}
                                showArrows
                                navigationTestId="botao_selecao_cartao"
                                getThumbnailTestId={(selectedCard) =>
                                  `thumbnail_cartao_ativo_${selectedCard.id}`
                                }
                              />
                              <div className="underline-bar" />
                              <Slider
                                {...settingsHolder}
                                ref={sliderRef}
                                className="card-slides"
                              >
                                {sortedHolderCards.map((card) => (
                                  <div key={card.id}>
                                    <CreditCardV2
                                      newClass="v2"
                                      card={card}
                                      isDependent={false}
                                      reloadCards={reloadAllCards}
                                      bloqCard={bloqCard}
                                      unBloqCard={unBloqCard}
                                    />
                                    <div className="centralizeStatus">
                                      <StatusCard card={card} />
                                    </div>
                                    <CardMenu
                                      productID={client?.product?.codigoProduto}
                                      clientID={client?.client?.id}
                                      isDependent={false}
                                      isBandeirado={!!isProdutoBandeirado}
                                      reloadCards={getDataFormatedCards}
                                      onVipagemSuccess={handleAfterVipagem}
                                      bloqCard={bloqCard}
                                      card={card}
                                      onExpandContentHeight={() =>
                                        sliderRef.current?.innerSlider?.forceUpdate()
                                      }
                                    />
                                  </div>
                                ))}
                              </Slider>
                            </>
                          ) : (
                            sortedHolderCards.map((card) => (
                              <React.Fragment key={card.id}>
                                <ThumbnailsMenu
                                  sliderRef={sliderRef}
                                  cards={sortedHolderCards}
                                  currentSlide={currentSlide}
                                  onClick={(index) =>
                                    sliderRef.current.slickGoTo(index)
                                  }
                                  title="Cartão"
                                  showAddButton={permissions?.includes(
                                    "view_button_generate_card_holder"
                                  )}
                                  onAddButtonTooltip="Adicionar Cartão Titular"
                                  onAddClick={handleGenerateCardHolder}
                                  getThumbnailTestId={(card) =>
                                    `thumbnail_cartao_ativo_${card.id}`
                                  }
                                />
                                <div className="underline-bar" />
                                <CreditCardV2
                                  newClass="v2"
                                  card={card}
                                  isDependent={false}
                                  reloadCards={reloadAllCards}
                                  bloqCard={bloqCard}
                                  unBloqCard={unBloqCard}
                                />
                                <div className="centralizeStatus">
                                  <StatusCard card={card} />
                                </div>
                                <CardMenu
                                  productID={client?.product?.codigoProduto}
                                  clientID={client?.client?.id}
                                  isDependent={false}
                                  isBandeirado={!!isProdutoBandeirado}
                                  reloadCards={getDataFormatedCards}
                                  onVipagemSuccess={handleAfterVipagem}
                                  bloqCard={bloqCard}
                                  card={card}
                                />
                              </React.Fragment>
                            ))
                          )}
                        </>
                      )}

                      {client?.client?.isEligibleForBrandCard && (
                        <CardsInTransition
                          reloadCards={getDataFormatedCards}
                          reloadDependantCards={reloadDependantCards}
                          bloqCard={bloqCard}
                          unBloqCard={unBloqCard}
                        />
                      )}
                    </S.CreditCardWrapper>
                  ),
                },
                {
                  id: "tab-cartao-adicional",
                  title: "Adicional",
                  content: (
                    <S.CreditCardWrapper>
                      {sortedDependentCards.length === 0 ? (
                        <div className="adicional__card">
                          <div className="adicional__card--header">
                            {permissions && permissions.includes("add") && (
                              <>
                                <div>
                                  <h2>Cartões</h2>
                                </div>
                                <div>
                                  <Tooltip title="Adicionar Cartão Adicional">
                                    <button
                                      className="plus-rounded"
                                      type="button"
                                      onClick={toggleAddModal}
                                    >
                                      <FaPlus />
                                    </button>
                                  </Tooltip>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="adicional__card--bar" />
                          <div className="adicional__card--container">
                            <div className="adicional__card--content">
                              <div>
                                <img
                                  src={magnifyingGlassIcon}
                                  alt="Lupa"
                                  className="magnifier-icon"
                                />
                              </div>
                              <p>Não possui cartão cadastrado </p>
                              {permissions && permissions.includes("add") && (
                                <button
                                  type="button"
                                  aria-label="btn-add-new"
                                  onClick={toggleAddModal}
                                >
                                  <span>
                                    <FaPlus /> Adicionar adicional
                                  </span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {sortedDependentCards.length > 1 ? (
                            <>
                              <ThumbnailsMenu
                                sliderRef={sliderRefDependent}
                                cards={sortedDependentCards}
                                currentSlide={currentSlideDependent}
                                onClick={(index) =>
                                  sliderRefDependent.current.slickGoTo(index)
                                }
                                title={
                                  sortedDependentCards.length > 1
                                    ? "Cartões Adicionais"
                                    : "Cartão Adicional"
                                }
                                showAddButton={permissions?.includes("add")}
                                onAddButtonTooltip="Adicionar Cartão Adicional"
                                onAddClick={toggleAddModal}
                                showArrows
                                navigationTestId="botao_selecao_cartao_adicional"
                                getThumbnailTestId={(card) =>
                                  `thumbnail_cartao_adicional_${card.id}`
                                }
                              />
                              <div className="underline-bar" />
                              <Slider
                                {...settingsDependent}
                                ref={sliderRefDependent}
                                className="card-slides"
                              >
                                {sortedDependentCards.map((card) => (
                                  <div key={card.id}>
                                    <CreditCardV2
                                      newClass="v2"
                                      card={card}
                                      isDependent
                                      reloadCards={reloadAllCards}
                                      bloqCard={bloqCardDependent}
                                      unBloqCard={unBloqCardDependent}
                                    />
                                    <div className="centralizeStatus">
                                      <StatusCard card={card} />
                                    </div>
                                    <CardMenu
                                      productID={client?.product?.codigoProduto}
                                      clientID={client?.client?.id}
                                      isDependent
                                      isBandeirado={!!isProdutoBandeirado}
                                      bloqCard={bloqCard}
                                      reloadCards={getDataFormatedCards}
                                      card={card}
                                      cancelCard={cancelCard}
                                      onExpandContentHeight={() =>
                                        sliderRefDependent.current?.innerSlider?.forceUpdate()
                                      }
                                    />
                                  </div>
                                ))}
                              </Slider>
                            </>
                          ) : (
                            sortedDependentCards.map((card) => (
                              <div key={card.id}>
                                <ThumbnailsMenu
                                  sliderRef={sliderRefDependent}
                                  cards={sortedDependentCards}
                                  currentSlide={currentSlideDependent}
                                  onClick={(index) =>
                                    sliderRefDependent.current.slickGoTo(index)
                                  }
                                  title="Cartão Adicional"
                                  showAddButton={permissions?.includes("add")}
                                  onAddButtonTooltip="Adicionar Cartão Adicional"
                                  onAddClick={toggleAddModal}
                                  getThumbnailTestId={(selectedCard) =>
                                    `thumbnail_cartao_adicional_${selectedCard.id}`
                                  }
                                />
                                <div className="underline-bar" />
                                <CreditCardV2
                                  newClass="v2"
                                  card={card}
                                  isDependent
                                  reloadCards={reloadAllCards}
                                  bloqCard={bloqCardDependent}
                                  unBloqCard={unBloqCardDependent}
                                />
                                <div className="centralizeStatus">
                                  <StatusCard card={card} />
                                </div>
                                <CardMenu
                                  productID={client?.product?.codigoProduto}
                                  clientID={client?.client?.id}
                                  isDependent
                                  isBandeirado={!!isProdutoBandeirado}
                                  bloqCard={bloqCard}
                                  reloadCards={getDataFormatedCards}
                                  card={card}
                                />
                              </div>
                            ))
                          )}
                        </>
                      )}
                    </S.CreditCardWrapper>
                  ),
                },
                invalidCardsHistory?.length > 0
                  ? {
                      id: "tab-cartao-historico",
                      title: "Histórico",
                      content: (
                        <S.CreditCardWrapper>
                          <ThumbnailsMenu
                            sliderRef={sliderRefHistory}
                            cards={sortedHistoryCards}
                            currentSlide={currentSlideHistory}
                            onClick={(index) => {
                              sliderRefHistory.current.slickGoTo(index);
                            }}
                            title="Histórico"
                            showArrows
                            navigationTestId="botao_selecao_cartao_historico"
                            getThumbnailTestId={(card) => {
                              if (card?.status === "INATIVO") {
                                return `thumbnail_cartao_inativo_${card.id}`;
                              }
                              if (card?.status === "CANCELADO") {
                                return `thumbnail_cartao_cancelado_${card.id}`;
                              }
                              if (card?.status === "CARTAO_INVALIDO") {
                                return `thumbnail_cartao_invalido_${card.id}`;
                              }
                              return undefined;
                            }}
                          />
                          <div className="underline-bar" />
                          <Slider
                            {...settingsHistory}
                            ref={sliderRefHistory}
                            className="card-slides"
                          >
                            {sortedHistoryCards.map((card) => (
                              <div key={card.id}>
                                <CreditCardV2
                                  card={card}
                                  isDependent={false}
                                  reloadCards={getDataFormatedCards}
                                  isHistoryCard
                                />
                                <div className="centralizeStatus">
                                  <StatusCard card={card} />
                                </div>
                                {invalidCardStatus.includes(card?.status) && (
                                  <HistoryBlockAction
                                    standalone
                                    card={card}
                                    isBandeirado={!!isProdutoBandeirado}
                                  />
                                )}
                              </div>
                            ))}
                          </Slider>
                        </S.CreditCardWrapper>
                      ),
                    }
                  : null,
              ].filter(Boolean)}
            />
          </Card>
        )}
      </S.WrapperCards>

      {isAddModalVisible && <AddCardModal closeModal={toggleAddModal} />}

      {isGenerateModalVisible && (
        <ConfirmAction
          isModalVisible={isGenerateModalVisible}
          closeModal={() => setIsGenerateModalVisible(false)}
          getDataFormatedCards={getDataFormatedCards}
          clientId={client?.client?.id}
          reloadCards={getDataFormatedCards}
        />
      )}
    </>
  );
}

export const Cards = memo(CardsComponent);
